import { Alert, Spin } from 'antd'
import { useEffect, useState } from 'react'
import { TbFolders } from 'react-icons/tb'
import { Navigate, useParams } from 'react-router-dom'

import { DISK_FILE_PARAM, diskFolderUrl, diskSharedUrl } from '../Helpers/diskRoutes'
import { diskErrorText } from '../Helpers/diskErrors'
import { diskBrowserStore } from '../Stores/DiskBrowserStore'
import { LoadDiskNodeTask } from '../Tasks/LoadDiskNodeTask'

import type { DiskNode } from '../Contracts/DiskNode'
import { withDiskFeature } from '../Helpers/diskFeature'

import type { IPageConfig } from '@/Core/Contracts/Loaders/IPageConfig'

const withFile = (url: string, uuid: string): string => `${url}?${DISK_FILE_PARAM}=${encodeURIComponent(uuid)}`

/**
 * Куди веде посилання на вузол. Простір у людини є — у простір. Немає
 * (доступ лише на вузлі чи предку) — у «Спільні зі мною»: папка — сама
 * папка, файл — у своїй папці, якщо та теж доступна (вона є в обрізаному
 * шляху `ancestors`), інакше поверх переліку спільних.
 */
export const diskNodeTarget = (node: DiskNode, hasStorage: boolean): string | null => {
    const storage = node.storage?.uuid

    if (hasStorage && storage) {
        return node.type === 'folder'
            ? diskFolderUrl(storage, node.parentUuid === null ? null : node.uuid)
            : withFile(diskFolderUrl(storage, node.parentUuid), node.uuid)
    }

    if (node.type === 'folder') return node.parentUuid === null ? null : diskSharedUrl(node.uuid)

    const parentVisible = (node.ancestors ?? []).some((ancestor) => ancestor.uuid === node.parentUuid)

    return withFile(diskSharedUrl(parentVisible ? node.parentUuid : null), node.uuid)
}

/**
 * Посилання на вузол диска за uuid — `/disk/node/:uuid` (сповіщення «з вами
 * поділились», `entityCode` `Disk.Node`; «Копіювати посилання» модалки
 * «Поділитися»): картка вузла → папка відкривається як папка, файл — у своїй
 * папці в переглядачі (`?file=`). Хто бачить вузол лише через доступ,
 * потрапляє в «Спільні зі мною» з крихтами від спільної папки.
 */
const NodeLinkPage = () => {
    const { uuid } = useParams()
    const [target, setTarget] = useState<string | null>(null)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        if (!uuid) return

        document.title = 'Диск'
        let alive = true

        Promise.all([LoadDiskNodeTask(uuid), diskBrowserStore.loadStorages()])
            .then(([node]) => {
                if (!alive) return

                const next = diskNodeTarget(node, diskBrowserStore.storageByUuid(node.storage?.uuid) !== null)
                if (next === null) {
                    setError('Простір не знайдено або у вас немає до нього доступу')

                    return
                }

                setTarget(next)
            })
            .catch((reason: unknown) => {
                if (alive) setError(diskErrorText(reason, 'Не вдалося відкрити посилання'))
            })

        return () => {
            alive = false
        }
    }, [uuid])

    if (target !== null) return <Navigate replace to={target} />

    if (error !== null) {
        return (
            <div style={{ padding: 16 }}>
                <Alert type="error" showIcon title={error} />
            </div>
        )
    }

    return (
        <div style={{ padding: 48, textAlign: 'center' }}>
            <Spin />
        </div>
    )
}

export default withDiskFeature(NodeLinkPage)

export const pageConfig: IPageConfig = {
    key: 'Disk.NodeLink',
    route: '/disk/node/:uuid',
    title: 'Диск',
    icon: <TbFolders />,
}
